import { useEffect } from 'react';

const SITE_ORIGIN = 'https://www.buildlabconsulting.com';
const DEFAULT_TITLE = 'BUILDLAB — Technology Consulting & Design';
const DEFAULT_DESC = 'BUILDLAB — Technology Consulting & Design';

const setMeta = (name, content) => {
    let tag = document.querySelector(`meta[name="${name}"]`);
    if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute('name', name);
        document.head.appendChild(tag);
    }
    tag.setAttribute('content', content);
    return tag;
};

const setOg = (property, content) => {
    let tag = document.querySelector(`meta[property="${property}"]`);
    if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute('property', property);
        document.head.appendChild(tag);
    }
    tag.setAttribute('content', content);
    return tag;
};

const setLink = (rel, href) => {
    let tag = document.querySelector(`link[rel="${rel}"]`);
    if (!tag) {
        tag = document.createElement('link');
        tag.setAttribute('rel', rel);
        document.head.appendChild(tag);
    }
    tag.setAttribute('href', href);
    return tag;
};

const INDEX_ROBOTS = 'index, follow, max-image-preview:large';
const NOINDEX_ROBOTS = 'noindex, follow';

const usePageSeo = ({ title, description, keywords, noindex = false, image, ogType }) => {
    useEffect(() => {
        const prevTitle = document.title;
        const descTag = document.querySelector('meta[name="description"]');
        const prevDesc = descTag?.getAttribute('content') || DEFAULT_DESC;
        const kwTag = document.querySelector('meta[name="keywords"]');
        const prevKw = kwTag?.getAttribute('content') || '';
        const robotsTag = document.querySelector('meta[name="robots"]');
        const prevRobots = robotsTag?.getAttribute('content') || INDEX_ROBOTS;
        const canonicalTag = document.querySelector('link[rel="canonical"]');
        const prevCanonical = canonicalTag?.getAttribute('href') || `${SITE_ORIGIN}/`;

        const fullTitle = title ? `${title} | BUILDLAB` : DEFAULT_TITLE;
        // Pages are served as directories (react-snap emits /page/index.html), so
        // the canonical 200-OK URL has a trailing slash. Normalize to match the
        // sitemap and avoid canonicals that point at a redirect.
        const rawPath = window.location.pathname;
        const normalizedPath = rawPath === '/' ? '/' : rawPath.replace(/\/+$/, '') + '/';
        const canonicalUrl = `${SITE_ORIGIN}${normalizedPath}`;

        document.title = fullTitle;
        if (description) setMeta('description', description);
        if (keywords) setMeta('keywords', keywords);

        setOg('og:title', fullTitle);
        if (description) setOg('og:description', description);
        setOg('og:type', ogType || 'website');
        setOg('og:site_name', 'BUILDLAB');
        setOg('og:url', canonicalUrl);

        setMeta('twitter:title', fullTitle);
        if (description) setMeta('twitter:description', description);

        // Optional social-share image. CRA-imported assets resolve to
        // root-relative hashed /static/media/ URLs — make them absolute so
        // social scrapers can fetch them.
        if (image) {
            const absImage = /^https?:\/\//.test(image) ? image : `${SITE_ORIGIN}${image}`;
            setOg('og:image', absImage);
            setMeta('twitter:image', absImage);
        }

        setMeta('robots', noindex ? NOINDEX_ROBOTS : INDEX_ROBOTS);

        setLink('canonical', canonicalUrl);

        return () => {
            document.title = prevTitle;
            if (descTag && prevDesc) descTag.setAttribute('content', prevDesc);
            if (kwTag && prevKw) kwTag.setAttribute('content', prevKw);
            if (robotsTag) robotsTag.setAttribute('content', prevRobots);
            if (canonicalTag) canonicalTag.setAttribute('href', prevCanonical);
        };
    }, [title, description, keywords, noindex, image, ogType]);
};

export default usePageSeo;
